import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  Group as PanelGroup,
  Panel,
  type PanelImperativeHandle,
  type PanelSize,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";

import { Values, ZIndex } from "@/constants";
import { useDevice } from "@/hooks/useDevice";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { useGlobalStore } from "@/state/useGlobalStore";
import { cn } from "@/components/ui/utils";

/** Shared flex-grow duration for panel open/close (see layout.css). */
const PANEL_SIZE_TRANSITION_MS = 200;
const PANEL_HANDLE_SIZE_TRANSITION = `width ${PANEL_SIZE_TRANSITION_MS}ms ease-in-out, min-width ${PANEL_SIZE_TRANSITION_MS}ms ease-in-out, height ${PANEL_SIZE_TRANSITION_MS}ms ease-in-out, min-height ${PANEL_SIZE_TRANSITION_MS}ms ease-in-out`;

// ---------------------------------------------------------------------------
// Context – allows nested components to read layout-wide settings
// ---------------------------------------------------------------------------

type AppLayoutContextType = {
  colors: ReturnType<typeof useLayoutColors>;
};

const AppLayoutContext = createContext<AppLayoutContextType | null>(null);

function useAppLayoutContext() {
  const ctx = useContext(AppLayoutContext);
  if (!ctx) {
    throw new Error(
      "AppLayout compound components must be rendered inside <AppLayout>",
    );
  }
  return ctx;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const panelAnimationTimers = new WeakMap<
  HTMLElement,
  ReturnType<typeof setTimeout>
>();

/** Suppress open/close transitions until the first layout pass completes. */
let suppressPanelSizeAnimations = true;

function releasePanelSizeAnimations() {
  if (!suppressPanelSizeAnimations) {
    return;
  }
  suppressPanelSizeAnimations = false;
}

/** Called after initial layout (and optional store rehydrate) before user-driven panel changes. */
export function releaseInitialPanelSizeAnimations() {
  releasePanelSizeAnimations();
}

export function scheduleInitialPanelSizeAnimationRelease() {
  requestAnimationFrame(() => {
    requestAnimationFrame(releaseInitialPanelSizeAnimations);
  });
}

function beginPanelSizeAnimation(panelEl: HTMLElement | null | undefined) {
  if (!panelEl || prefersReducedMotion() || suppressPanelSizeAnimations) {
    return;
  }
  const existing = panelAnimationTimers.get(panelEl);
  if (existing != null) {
    clearTimeout(existing);
  }
  panelEl.setAttribute("data-panel-animating", "");
  // Commit the transition rule before react-resizable-panels updates flex-grow.
  void panelEl.offsetWidth;
  const timer = setTimeout(() => {
    panelEl.removeAttribute("data-panel-animating");
    panelAnimationTimers.delete(panelEl);
  }, PANEL_SIZE_TRANSITION_MS);
  panelAnimationTimers.set(panelEl, timer);
}

function endPanelSizeAnimation(panelEl: HTMLElement | null | undefined) {
  if (!panelEl) {
    return;
  }
  const existing = panelAnimationTimers.get(panelEl);
  if (existing != null) {
    clearTimeout(existing);
    panelAnimationTimers.delete(panelEl);
  }
  panelEl.removeAttribute("data-panel-animating");
}

/** Imperative collapse/expand with the open/close flex-grow animation. */
export function syncPanelCollapsed(
  panelRef: PanelImperativeHandle | null | undefined,
  panelEl: HTMLElement | null | undefined,
  isMinimized: boolean,
  animate = true,
  /** Pixels to open to, rather than the size the panel last had. */
  openSize?: number | null,
) {
  if (!panelRef) {
    return;
  }
  try {
    if (panelRef.isCollapsed() === isMinimized) {
      return;
    }
    if (animate) {
      beginPanelSizeAnimation(panelEl);
    }
    if (isMinimized) {
      panelRef.collapse();
    } else if (openSize) {
      panelRef.resize(openSize);
    } else {
      panelRef.expand();
    }
  } catch {
    // Panel group may not be registered yet on mount.
  }
}

function usePanelCollapseSync(
  panelRef: RefObject<PanelImperativeHandle | null> | undefined,
  elementRef: RefObject<HTMLDivElement | null>,
  isMinimized: boolean | undefined,
  openSize?: number | null,
) {
  useEffect(() => {
    if (isMinimized === undefined) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      syncPanelCollapsed(
        panelRef?.current,
        elementRef.current,
        isMinimized,
        true,
        openSize,
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [panelRef, elementRef, isMinimized, openSize]);
}

/**
 * Locks collapsible panel children to the last open pixel width while
 * collapse/expand animates, so content is clipped instead of reflowed.
 */
function usePanelOpenCloseClip(
  panelRef: RefObject<PanelImperativeHandle | null> | undefined,
  elementRef: RefObject<HTMLDivElement | null>,
  fallbackOpenWidthPx: number,
) {
  const openWidthPxRef = useRef(0);
  const wasCollapsedRef = useRef<boolean | null>(null);
  const isClippingRef = useRef(false);
  const [clipWidthPx, setClipWidthPx] = useState<number | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearClip = useCallback(() => {
    if (clearTimerRef.current != null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
    isClippingRef.current = false;
    setClipWidthPx(null);
    endPanelSizeAnimation(elementRef.current);
  }, [elementRef]);

  const startClip = useCallback((widthPx: number) => {
    if (widthPx <= 0 || prefersReducedMotion()) {
      return;
    }
    isClippingRef.current = true;
    setClipWidthPx(widthPx);
    if (clearTimerRef.current != null) {
      clearTimeout(clearTimerRef.current);
    }
    clearTimerRef.current = setTimeout(() => {
      isClippingRef.current = false;
      setClipWidthPx(null);
      clearTimerRef.current = null;
    }, PANEL_SIZE_TRANSITION_MS);
  }, []);

  useEffect(
    () => () => {
      if (clearTimerRef.current != null) {
        clearTimeout(clearTimerRef.current);
      }
    },
    [],
  );

  const onResizeForClip = useCallback(
    (panelSize: PanelSize) => {
      const group = elementRef.current?.closest("[data-group]");
      if (group?.querySelector('[data-separator="active"]')) {
        clearClip();
        const collapsed = panelRef?.current?.isCollapsed() ?? false;
        wasCollapsedRef.current = collapsed;
        if (!collapsed && panelSize.inPixels > 0) {
          openWidthPxRef.current = panelSize.inPixels;
        }
        return;
      }

      const collapsed = panelRef?.current?.isCollapsed() ?? false;

      if (wasCollapsedRef.current === null) {
        wasCollapsedRef.current = collapsed;
        if (!collapsed && panelSize.inPixels > 0) {
          openWidthPxRef.current = panelSize.inPixels;
        }
        return;
      }

      if (wasCollapsedRef.current !== collapsed) {
        const lockWidth =
          openWidthPxRef.current > 0
            ? openWidthPxRef.current
            : fallbackOpenWidthPx > 0
              ? fallbackOpenWidthPx
              : panelSize.inPixels;
        startClip(lockWidth);
        wasCollapsedRef.current = collapsed;
        return;
      }

      if (!collapsed && !isClippingRef.current && panelSize.inPixels > 0) {
        openWidthPxRef.current = panelSize.inPixels;
      }
    },
    [panelRef, elementRef, clearClip, startClip, fallbackOpenWidthPx],
  );

  return { clipWidthPx, onResizeForClip };
}

function PanelClipShell({
  clipWidthPx,
  children,
}: {
  clipWidthPx: number | null;
  children: React.ReactNode;
}) {
  // Always render the same wrapper element. Switching between a Fragment and a
  // ``div`` when clipping starts/ends remounts panel children (e.g. TipTap /
  // thread empty suggestions) and looks like the panel "loads twice".
  const clipping = clipWidthPx != null;
  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
        ...(clipping
          ? {
              width: clipWidthPx,
              minWidth: clipWidthPx,
              flexShrink: 0,
            }
          : {
              flex: 1,
              minWidth: 0,
              width: "100%",
            }),
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <AppLayout> – Root container (100vw × 100dvh, overflow hidden)
// ---------------------------------------------------------------------------

export function AppLayout({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const colors = useLayoutColors();
  const rootRef = useRef<HTMLDivElement>(null);

  // A phone's keyboard covers the page rather than shortening it, and iOS
  // slides the whole page up to bring the field being typed in above it: the
  // top bar went off the screen and a view's footer stood half way up it. The
  // app takes the height of what is left above the keyboard instead - the
  // visual viewport - and stays level with it, as a phone's own apps do: its
  // bars where they were, its footer on the keyboard. Zoomed in by a pinch,
  // the visual viewport is a window onto the page, not room the keyboard
  // left, and the app keeps its own height.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const fit = () => {
      const root = rootRef.current;
      if (!root) return;
      if (viewport.scale > 1.01) {
        root.style.removeProperty("height");
        root.style.removeProperty("min-height");
        root.style.removeProperty("transform");
        return;
      }
      root.style.height = `${viewport.height}px`;
      root.style.minHeight = `${viewport.height}px`;
      root.style.transform = viewport.offsetTop
        ? `translateY(${viewport.offsetTop}px)`
        : "";
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };
    fit();
    viewport.addEventListener("resize", fit);
    viewport.addEventListener("scroll", fit);
    return () => {
      viewport.removeEventListener("resize", fit);
      viewport.removeEventListener("scroll", fit);
    };
  }, []);

  useEffect(() => {
    const release = () => scheduleInitialPanelSizeAnimationRelease();

    if (useGlobalStore.persist.hasHydrated()) {
      release();
      return;
    }

    return useGlobalStore.persist.onFinishHydration(release);
  }, []);

  return (
    <AppLayoutContext.Provider value={{ colors }}>
      <div
        ref={rootRef}
        data-testid="app-layout"
        // The dynamic height: on a phone, 100vh runs on under the browser's
        // own toolbar, and the bottom of the page is drawn behind it.
        // Clear of a phone's notch and rounded corners (``viewport-fit=
        // cover``); bars along the bottom keep clear of its home indicator
        // themselves, so their ground runs to the screen's edge.
        className={cn(
          "flex flex-row h-dvh min-h-dvh max-h-dvh box-border pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] w-screen min-w-screen max-w-[100vw] flex-nowrap overflow-hidden",
          className,
        )}
      >
        {children}
      </div>
    </AppLayoutContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutPanelGroup> – Resizable panel group (horizontal or vertical)
// ---------------------------------------------------------------------------

export function AppLayoutPanelGroup({
  direction = "horizontal",
  id,
  defaultLayout,
  disabled = false,
  children,
  style,
  groupRef,
  onLayoutChange,
  onLayoutChanged,
}: {
  direction?: "horizontal" | "vertical";
  id?: string;
  defaultLayout?: Record<string, number>;
  disabled?: boolean;
  children: React.ReactNode;
  /** Its own style, over its own. */
  style?: React.CSSProperties;
  groupRef?: RefObject<
    import("react-resizable-panels").GroupImperativeHandle | null
  >;
  onLayoutChange?: (layout: Record<string, number>) => void;
  onLayoutChanged?: (
    layout: Record<string, number>,
    meta: { isUserInteraction: boolean },
  ) => void;
}) {
  return (
    <PanelGroup
      id={id}
      orientation={direction}
      defaultLayout={defaultLayout}
      disabled={disabled}
      groupRef={groupRef}
      onLayoutChange={onLayoutChange}
      onLayoutChanged={onLayoutChanged}
      style={{
        // What holds it left to it as a flex item, across and along, never
        // the library's ``height: 100%``: Safari lays a percentage height out
        // again through every flex box under it, at every key typed.
        flex: "1 1 0%",
        alignSelf: "stretch",
        height: "auto",
        width: "auto",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </PanelGroup>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutPanelResizeHandle> – Styled resize handle between panels
// ---------------------------------------------------------------------------

export function AppLayoutPanelResizeHandle({
  style,
  disabled = false,
  hideDivider = false,
  orientation = "horizontal",
}: {
  /** Its own style, over its own. */
  style?: React.CSSProperties;
  disabled?: boolean;
  /** Keep resize hit target; collapse the layout seam (no visible divider). */
  hideDivider?: boolean;
  /**
   * Matches ``PanelGroup`` orientation: ``horizontal`` panels use a
   * vertical divider; ``vertical`` stacks use a horizontal divider.
   */
  orientation?: "horizontal" | "vertical";
} = {}) {
  const isVerticalStack = orientation === "vertical";
  const seamlessDivider = hideDivider && !disabled;
  // When ``disabled`` we keep the handle mounted (the panels library
  // tracks panels by sibling order, so unmounting handles desyncs the
  // surrounding panels' constraints) but collapse it to zero-width and
  // suppress hover/active highlight so it neither resizes nor visually
  // responds to mouse events from a sibling drag in progress.
  const disabledStyle: React.CSSProperties | undefined = disabled
    ? isVerticalStack
      ? {
          height: "0px",
          minHeight: "0px",
          pointerEvents: "none",
        }
      : {
          width: "0px",
          minWidth: "0px",
          pointerEvents: "none",
        }
    : undefined;
  return (
    <PanelResizeHandle
      disabled={disabled}
      style={{
        width: isVerticalStack ? "100%" : seamlessDivider ? "0px" : "1px",
        minWidth: isVerticalStack ? "100%" : seamlessDivider ? "0px" : "1px",
        height: isVerticalStack ? (seamlessDivider ? "0px" : "1px") : undefined,
        minHeight: isVerticalStack
          ? seamlessDivider
            ? "0px"
            : "1px"
          : undefined,
        flexShrink: 0,
        cursor: isVerticalStack ? "row-resize" : "col-resize",
        position: "relative",
        zIndex: ZIndex.APP.PANEL_RESIZE_HANDLE,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
        outline: "none",
        transition: PANEL_HANDLE_SIZE_TRANSITION,
        ...style,
        ...disabledStyle,
      }}
    >
      <div
        className={cn(
          isVerticalStack
            ? [
                "w-full min-w-8 [transition:height_0.15s_ease,background-color_0.15s_ease]",
                seamlessDivider ? "h-2 absolute -top-1 left-0" : "h-px",
                !disabled &&
                  !hideDivider &&
                  "hover:h-1 hover:mt-[-1.5px] [[data-separator='hover']_&]:h-1 [[data-separator='hover']_&]:mt-[-1.5px] [[data-separator='active']_&]:h-1 [[data-separator='active']_&]:mt-[-1.5px]",
              ]
            : [
                "h-full min-h-8 [transition:width_0.15s_ease,background-color_0.15s_ease]",
                seamlessDivider ? "w-2 absolute -left-1 top-0" : "w-px",
                !disabled &&
                  !hideDivider &&
                  "hover:w-1 hover:ml-[-1.5px] [[data-separator='hover']_&]:w-1 [[data-separator='hover']_&]:ml-[-1.5px] [[data-separator='active']_&]:w-1 [[data-separator='active']_&]:ml-[-1.5px]",
              ],
          hideDivider
            ? "bg-transparent hover:bg-transparent [[data-separator='hover']_&]:bg-transparent [[data-separator='active']_&]:bg-transparent"
            : [
                "bg-border",
                // Blue under the pointer and while it is dragged, as Joy's
                // primary drew it: the theme's own blue, the neutral palette
                // having none of its own.
                !disabled &&
                  "hover:bg-enum-blue-solid [[data-separator='hover']_&]:bg-enum-blue-solid [[data-separator='active']_&]:bg-enum-blue-solid",
              ],
          disabled && "hidden",
        )}
      />
    </PanelResizeHandle>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutSidebar> – Resizable sidebar panel (use inside AppLayoutPanelGroup)
// ---------------------------------------------------------------------------

export function AppLayoutSidebar({
  children,
  defaultSize = Values.SIDEBAR_WIDTH,
  minSize = Values.MINIMIZED_SIDEBAR_WIDTH * 3,
  maxSize,
  collapsible = true,
  collapsedSize = Values.MINIMIZED_SIDEBAR_WIDTH,
  panelRef,
  onCollapse,
  onExpand,
  isMinimized,
  id = "sidebar",
  style,
}: {
  children: React.ReactNode;
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  collapsible?: boolean;
  collapsedSize?: number;
  panelRef?: RefObject<PanelImperativeHandle | null>;
  onCollapse?: () => void;
  onExpand?: () => void;
  isMinimized?: boolean;
  id?: string;
  /** Its own style, over its own. */
  style?: React.CSSProperties;
}) {
  const { colors } = useAppLayoutContext();
  const effectiveMaxSize = maxSize ?? defaultSize * 1.5;
  const wasCollapsedRef = useRef(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const { clipWidthPx, onResizeForClip } = usePanelOpenCloseClip(
    panelRef,
    elementRef,
    typeof defaultSize === "number" ? defaultSize : Values.SIDEBAR_WIDTH,
  );
  usePanelCollapseSync(panelRef, elementRef, isMinimized);

  const handleResize = useCallback(
    (panelSize: PanelSize) => {
      onResizeForClip(panelSize);
      if (!panelRef?.current) return;
      const isNowCollapsed = panelRef.current.isCollapsed();
      if (isNowCollapsed && !wasCollapsedRef.current) {
        wasCollapsedRef.current = true;
        onCollapse?.();
      } else if (!isNowCollapsed && wasCollapsedRef.current) {
        wasCollapsedRef.current = false;
        onExpand?.();
      }
    },
    [panelRef, onCollapse, onExpand, onResizeForClip],
  );

  return (
    <Panel
      id={id}
      panelRef={panelRef}
      elementRef={elementRef}
      defaultSize={defaultSize}
      minSize={minSize}
      maxSize={effectiveMaxSize}
      collapsible={collapsible}
      collapsedSize={collapsedSize}
      onResize={handleResize}
      style={{
        backgroundColor: colors.sidebar,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        isolation: "isolate",
        ...style,
      }}
    >
      <PanelClipShell clipWidthPx={collapsible ? clipWidthPx : null}>
        {children}
      </PanelClipShell>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutMainPanel> – Main content area (use inside AppLayoutPanelGroup)
// ---------------------------------------------------------------------------

export function AppLayoutMainPanel({
  children,
  id = "main",
  style,
}: {
  children: React.ReactNode;
  id?: string;
  /** Its own style, over its own. */
  style?: React.CSSProperties;
}) {
  return (
    <Panel
      id={id}
      defaultSize={85}
      minSize={20}
      style={{
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        isolation: "isolate",
        ...style,
      }}
    >
      {children}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutContentPanel> – Content area panel (use inside AppLayoutPanelGroup)
// ---------------------------------------------------------------------------

export function AppLayoutContentPanel({
  children,
  id = "content",
  minSize = 400,
  defaultSize,
  groupResizeBehavior,
  style,
}: {
  children: React.ReactNode;
  id?: string;
  minSize?: number | string;
  /** Unset, the content takes what the group's other panels leave it. */
  defaultSize?: number | string;
  groupResizeBehavior?: "preserve-relative-size" | "preserve-pixel-size";
  /** Its own style, over its own. */
  style?: React.CSSProperties;
}) {
  const { colors } = useAppLayoutContext();

  return (
    <Panel
      id={id}
      defaultSize={defaultSize}
      minSize={minSize}
      {...(groupResizeBehavior !== undefined ? { groupResizeBehavior } : {})}
      style={{
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.content,
        isolation: "isolate",
        ...style,
      }}
    >
      {children}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutPanel> – Right-hand panel (use inside AppLayoutPanelGroup)
// ---------------------------------------------------------------------------

export const PANEL_MIN_SIZE_PX = 400;

export function AppLayoutPanel({
  children,
  defaultSize = 500,
  minSize = PANEL_MIN_SIZE_PX,
  maxSize,
  collapsible = false,
  collapsedSize = 0,
  disabled = false,
  groupResizeBehavior,
  onCollapse,
  onExpand,
  onResize,
  panelRef,
  isMinimized,
  openSize,
  id = "panel",
  style,
}: {
  children: React.ReactNode;
  defaultSize?: number | string;
  minSize?: number | string;
  maxSize?: number | string;
  collapsible?: boolean;
  collapsedSize?: number | string;
  /** Blocks direct and indirect resize while the panel is store-closed. */
  disabled?: boolean;
  groupResizeBehavior?: "preserve-relative-size" | "preserve-pixel-size";
  onCollapse?: () => void;
  onExpand?: () => void;
  onResize?: (
    panelSize: { asPercentage: number; inPixels: number },
    id?: string | number,
    prevPanelSize?: { asPercentage: number; inPixels: number },
  ) => void;
  panelRef?: RefObject<PanelImperativeHandle | null>;
  isMinimized?: boolean;
  /** Pixels it opens to, rather than the size it had when it was shut. */
  openSize?: number | null;
  id?: string;
  /** Its own style, over its own. */
  style?: React.CSSProperties;
}) {
  const { colors } = useAppLayoutContext();
  const wasCollapsedRef = useRef(false);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const fallbackOpenWidthPx =
    typeof defaultSize === "number"
      ? defaultSize
      : typeof minSize === "number"
        ? minSize
        : PANEL_MIN_SIZE_PX;
  const { clipWidthPx, onResizeForClip } = usePanelOpenCloseClip(
    panelRef,
    elementRef,
    fallbackOpenWidthPx,
  );
  usePanelCollapseSync(panelRef, elementRef, isMinimized, openSize);

  const handleResize = useCallback(
    (
      panelSize: PanelSize,
      resizeId?: string | number,
      prevPanelSize?: PanelSize,
    ) => {
      if (collapsible) {
        onResizeForClip(panelSize);
      }
      onResize?.(panelSize, resizeId, prevPanelSize);
      if (!panelRef?.current) return;
      const isNowCollapsed = panelRef.current.isCollapsed();
      if (isNowCollapsed && !wasCollapsedRef.current) {
        wasCollapsedRef.current = true;
        onCollapse?.();
      } else if (!isNowCollapsed && wasCollapsedRef.current) {
        wasCollapsedRef.current = false;
        onExpand?.();
      }
    },
    [collapsible, onCollapse, onExpand, onResize, onResizeForClip, panelRef],
  );

  return (
    <Panel
      id={id}
      panelRef={panelRef}
      elementRef={elementRef}
      defaultSize={defaultSize}
      minSize={minSize}
      {...(maxSize !== undefined ? { maxSize } : {})}
      {...(groupResizeBehavior !== undefined ? { groupResizeBehavior } : {})}
      collapsible={collapsible}
      disabled={disabled}
      onResize={handleResize}
      collapsedSize={collapsedSize}
      style={{
        backgroundColor: colors.panel,
        height: "100%",
        display: "flex",
        flexDirection: "row",
        overflowX: "hidden",
        overflowY: "auto",
        borderColor: "divider",
        isolation: "isolate",
        ...style,
      }}
    >
      <PanelClipShell clipWidthPx={collapsible ? clipWidthPx : null}>
        {children}
      </PanelClipShell>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutOverlayPanel> – A panel drawn over the content, not beside it
// ---------------------------------------------------------------------------
//
// How a phone shows the node panel and the thread: there is no room to put
// them beside the page, so each covers it while open and the page is under it
// again once shut. Its parent is positioned; the topbar above stays in reach,
// with the toggles that shut it.

export function AppLayoutOverlayPanel({
  children,
  id,
}: {
  children: React.ReactNode;
  id: string;
}) {
  return (
    <div
      id={id}
      // Its foot - the thread's composer - clear of the home indicator.
      className="absolute inset-0 z-30 flex flex-col overflow-hidden bg-background isolate pb-[env(safe-area-inset-bottom)]"
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutPanelSync> – Syncs panel collapse state with store
// ---------------------------------------------------------------------------

export function AppLayoutPanelSync({
  panelRef,
  panelId,
  isMinimized,
}: {
  panelRef: RefObject<PanelImperativeHandle | null>;
  panelId: string;
  isMinimized: boolean;
}) {
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const panelEl = document.getElementById(panelId) as HTMLElement | null;
      syncPanelCollapsed(panelRef?.current, panelEl, isMinimized);
    });
    return () => cancelAnimationFrame(frame);
  }, [panelRef, panelId, isMinimized]);
  return null;
}

// ---------------------------------------------------------------------------
// <AppLayoutGroup> – Flex container with configurable orientation
// ---------------------------------------------------------------------------

export function AppLayoutGroup({
  orientation = "vertical",
  children,
  className,
}: {
  orientation?: "horizontal" | "vertical";
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex",
        orientation === "horizontal" ? "flex-row" : "flex-col",
        "grow shrink min-w-0 min-h-0 overflow-hidden flex-nowrap relative",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutTopbar> – Fixed-height horizontal strip
// ---------------------------------------------------------------------------

export function AppLayoutTopbar({
  children,
  height = Values.TOPBAR_HEIGHT,
  borderBottom = true,
}: {
  children?: React.ReactNode;
  height?: number;
  borderBottom?: boolean;
}) {
  const { colors } = useAppLayoutContext();

  return (
    <div
      className={cn(
        "flex flex-row items-stretch w-full shrink-0 relative flex-nowrap overflow-visible",
        borderBottom &&
          "[border-bottom-width:1px] [border-bottom-style:solid] border-border",
      )}
      style={{
        height: height + "px",
        minHeight: height + "px",
        maxHeight: height + "px",
        backgroundColor: colors.topbar,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// <AppLayoutContent> – Fills remaining space, scrollable by default
// ---------------------------------------------------------------------------

const MemoizedChildren = memo(function MemoizedChildren({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
});

export function AppLayoutContent({
  children,
  scrollable = true,
  contentRef,
  className,
}: {
  children: React.ReactNode;
  scrollable?: boolean;
  contentRef?: React.RefObject<HTMLDivElement>;
  className?: string;
}) {
  return (
    <div
      ref={contentRef}
      className={cn(
        "relative grow shrink min-h-0 min-w-0 w-full overflow-x-hidden",
        scrollable ? "overflow-y-auto" : "overflow-y-hidden",
        "flex flex-col bg-muted dark:bg-[#151719] @container [container-name:content]",
        className,
      )}
    >
      <MemoizedChildren>{children}</MemoizedChildren>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content areas – a view's own areas, side by side in its content
// ---------------------------------------------------------------------------
//
// A view that needs more than one area lays them out in its content with
// ``AppLayoutContentAreas``, each an ``AppLayoutContentArea``, with a handle
// between each one and the next. An area's width is a share of the content,
// not a count of pixels: as the content widens or narrows - the thread
// opening beside it, the window resized - every area keeps its share, and the
// ratio between them holds.
//
// An area's toolbar - its search, its tabs - is the layout's too: the bar is
// drawn across the top of the area whatever the area shows below it, loading,
// empty or not there yet, so what is in it is always to hand.
//
// A phone - or a window as narrow - has no room for them side by side: there
// the areas are one at a time, each the width of the content, and ``shown``
// names the one in view.
// The others stay put under it, so going back to one finds it as it was left.
// A view whose own content is too narrow for its areas - the thread open
// beside it on a laptop - says so, and has them one at a time as well.

/** The area in view while they are one at a time; ``null`` while side by side. */
const AppLayoutContentAreasContext = createContext<string | null>(null);

export function AppLayoutContentAreas({
  id,
  shown,
  isOneAtATime = false,
  children,
}: {
  id: string;
  /** The area in view while they are one at a time, by its ``id``. */
  shown: string;
  /** One at a time on a wide screen too: the view's content too narrow for them side by side. */
  isOneAtATime?: boolean;
  children: React.ReactNode;
}) {
  const { isNarrow } = useDevice();
  const areas = React.Children.toArray(children);
  if (isNarrow || isOneAtATime) {
    return (
      <AppLayoutContentAreasContext.Provider value={shown}>
        <div className="relative flex-1 min-w-0 min-h-0 overflow-hidden">
          {areas}
        </div>
      </AppLayoutContentAreasContext.Provider>
    );
  }
  return (
    <AppLayoutPanelGroup id={id} direction="horizontal">
      {areas.flatMap((area, index) =>
        index === 0
          ? [area]
          : [
              <AppLayoutPanelResizeHandle key={`${id}-handle-${index}`} />,
              area,
            ],
      )}
    </AppLayoutPanelGroup>
  );
}

export function AppLayoutContentArea({
  id,
  defaultSize,
  minSize,
  toolbar,
  children,
}: {
  id: string;
  /** Its share of the content to begin with, as a percentage (``"40%"``). */
  defaultSize: string;
  /** How narrow it may be dragged, in pixels. */
  minSize?: number;
  /** What goes in the bar across its top; no bar without it. */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  const shown = useContext(AppLayoutContentAreasContext);
  const column = (
    // Its own column: the panel lays out what it is given, but not as a bar
    // over what is under it.
    <div
      // The panel carries the id side by side; one at a time, there is none.
      id={shown !== null ? id : undefined}
      // One at a time: each over the others, and only the one in view seen.
      // Hidden rather than taken out, so a list keeps where it was scrolled.
      className={cn(
        "h-full w-full min-w-0 min-h-0 flex flex-col overflow-hidden",
        shown !== null &&
          (shown === id
            ? "absolute inset-0 visible"
            : "absolute inset-0 invisible"),
      )}
    >
      {toolbar !== undefined ? (
        <div
          data-app-layout-area-toolbar=""
          className="h-12 shrink-0 min-w-0 flex items-stretch box-border [border-bottom-width:1px] [border-bottom-style:solid] border-border bg-background"
        >
          {toolbar}
        </div>
      ) : null}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
  if (shown !== null) return column;
  return (
    <Panel
      id={id}
      defaultSize={defaultSize}
      {...(minSize !== undefined ? { minSize } : {})}
      style={{ minWidth: 0, minHeight: 0, overflow: "hidden" }}
    >
      {column}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Footer – a bar along the bottom of the view's content
// ---------------------------------------------------------------------------
//
// What a view does to the record it has open - hold it, review it, accept it -
// runs along the bottom of the view's own content, across however many areas
// that is divided into, and never under the node panel or the thread, which
// are the layout's. Two parts:
//
//   - ``AppLayoutFooterArea`` is the view's content column, and keeps the
//     footer's room at its foot - in the flow, so what is above ends where
//     the footer starts, however tall it grows.
//   - ``AppLayoutFooter`` is written by the view, wherever in it the bar's
//     state lives, and drawn in that room. It is portalled, so it keeps every
//     context it was written in. With no area round it, it is drawn where it
//     was written.

/** The footer's room: ``undefined`` outside an area, ``null`` until laid out. */
const AppLayoutFooterContext = createContext<HTMLDivElement | null | undefined>(
  undefined,
);

export function AppLayoutFooterArea({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  // Held in state rather than a ref: the footer has to render again once
  // there is somewhere to draw it.
  const [room, setRoom] = useState<HTMLDivElement | null>(null);
  return (
    <AppLayoutFooterContext.Provider value={room}>
      <AppLayoutGroup orientation="vertical" className={className}>
        {children}
        <div
          ref={setRoom}
          data-app-layout-footer-room=""
          className="shrink-0"
        />
      </AppLayoutGroup>
    </AppLayoutFooterContext.Provider>
  );
}

export function AppLayoutFooter({ children }: { children: React.ReactNode }) {
  const room = useContext(AppLayoutFooterContext);
  const bar = (
    <div
      data-testid="app-layout-footer"
      // Its ground to the screen's foot, what it holds clear of a phone's
      // home indicator.
      className="min-w-0 pb-[env(safe-area-inset-bottom)] [border-top-width:1px] [border-top-style:solid] border-border bg-background"
    >
      {children}
    </div>
  );
  if (room === undefined) return bar;
  if (room === null) return null;
  return createPortal(bar, room);
}
